import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import {
  BookOpen, Plus, Search, Edit3, Trash2, Save, X, Download,
  Package, Wrench, Box, Tag, AlertCircle, ChevronRight, Boxes
} from "lucide-react";
import { toast } from "sonner";
import { confirmAction } from "@/lib/confirm";

const WORK_CATS = [
  { value: "preparation", label: "Préparation" },
  { value: "peinture", label: "Peinture" },
  { value: "revetement_mural", label: "Revêtement mural" },
  { value: "revetement_sol", label: "Revêtement de sol" },
  { value: "platrerie", label: "Plâtrerie" },
  { value: "menuiserie", label: "Menuiserie" },
  { value: "electricite", label: "Électricité" },
  { value: "plomberie", label: "Plomberie" },
  { value: "isolation", label: "Isolation" },
  { value: "ravalement", label: "Ravalement" },
  { value: "demolition", label: "Démolition" },
  { value: "nettoyage", label: "Nettoyage" },
  { value: "divers", label: "Divers" },
];

const MAT_CATS = [
  { value: "peinture", label: "Peinture" },
  { value: "colle", label: "Colle" },
  { value: "toile_papier", label: "Toile / papier peint" },
  { value: "enduit", label: "Enduit / mortier" },
  { value: "fixation", label: "Fixation / quincaillerie" },
  { value: "outillage_consommable", label: "Outillage consommable" },
  { value: "protection", label: "Protection / bâchage" },
  { value: "diluant", label: "Diluant / solvant" },
  { value: "primaire", label: "Primaire / sous-couche" },
  { value: "vernis", label: "Vernis / lasure" },
  { value: "joint", label: "Joint / silicone" },
  { value: "divers", label: "Divers" },
];

const UNITS = ["m²", "ml", "u", "forfait", "h", "kg", "L", "g", "ml (litre)", "pot", "sac"];
const MAT_UNITS = ["L", "kg", "g", "m²", "ml", "u", "pot", "sac"];

const fmtEUR = (n) =>
  new Intl.NumberFormat("fr-FR", { style: "currency", currency: "EUR", maximumFractionDigits: 2 }).format(
    Number.isFinite(Number(n)) ? Number(n) : 0
  );

const findLabel = (arr, v) => arr.find((x) => x.value === v)?.label || v;

export default function Library() {
  const location = useLocation();
  const navigate = useNavigate();
  // Lecture du paramètre URL ?tab=... pour pré-sélectionner l'onglet
  // (utilisé par Stock.jsx "Ouvrir bibliothèque" → /library?tab=articles)
  const initialTab = (() => {
    const p = new URLSearchParams(location.search);
    const t = p.get("tab");
    return ["ouvrages", "materiaux", "articles"].includes(t) ? t : "ouvrages";
  })();
  const [tab, setTab] = useState(initialTab); // ouvrages | materiaux | articles
  const [items, setItems] = useState([]);
  const [materials, setMaterials] = useState([]);
  const [hourlyRate, setHourlyRate] = useState(0);
  const [defaultMargin, setDefaultMargin] = useState(30);
  const [q, setQ] = useState("");
  const [catFilter, setCatFilter] = useState("");
  const [openWi, setOpenWi] = useState(null); // null | "new" | "new-article" | <object>
  const [stockEditor, setStockEditor] = useState(null); // article object | null
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [selectedMatIds, setSelectedMatIds] = useState(new Set());
  const [importWizard, setImportWizard] = useState(null); // {rows, hasHeader}

  // Sync URL <-> tab (préserve l'état navigable)
  const changeTab = (id) => {
    setTab(id); setCatFilter(""); setQ(""); setSelectedIds(new Set()); setSelectedMatIds(new Set());
    const p = new URLSearchParams(location.search);
    if (id === "ouvrages") p.delete("tab"); else p.set("tab", id);
    navigate({ pathname: location.pathname, search: p.toString() }, { replace: true });
  };

  const loadAll = async () => {
    const [w, m] = await Promise.all([api.get("/work-items"), api.get("/materials")]);
    setItems(w.data.items);
    setHourlyRate(w.data.hourly_break_even);
    setDefaultMargin(w.data.default_margin_pct);
    setMaterials(m.data);
  };

  useEffect(() => { loadAll(); }, []);

  // Filtrage selon l'onglet : ouvrages = kind != "article", articles = kind == "article"
  const filteredItems = useMemo(() => items.filter((it) => {
    const kind = it.kind || "ouvrage";
    if (tab === "ouvrages" && kind === "article") return false;
    if (tab === "articles" && kind !== "article") return false;
    if (catFilter && it.category !== catFilter) return false;
    if (!q) return true;
    const s = `${it.code} ${it.description}`.toLowerCase();
    return s.includes(q.toLowerCase());
  }), [items, q, catFilter, tab]);

  const ouvragesCount = items.filter((it) => (it.kind || "ouvrage") !== "article").length;
  const articlesCount = items.filter((it) => it.kind === "article").length;

  const filteredMaterials = useMemo(() => materials.filter((m) => {
    if (catFilter && m.category !== catFilter) return false;
    if (!q) return true;
    const s = `${m.name} ${m.supplier || ""}`.toLowerCase();
    return s.includes(q.toLowerCase());
  }), [materials, q, catFilter]);

  const exportCsv = () => {
    const tok = document.cookie.split("session_token=")[1]?.split(";")[0] || "";
    window.location.href = `${process.env.REACT_APP_BACKEND_URL}/api/work-items/export.csv${tok ? `?token=${tok}` : ""}`;
  };

  const toggleSelected = (id) => {
    const next = new Set(selectedIds);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    setSelectedIds(next);
  };

  const toggleSelectAll = () => {
    if (selectedIds.size === filteredItems.length) setSelectedIds(new Set());
    else setSelectedIds(new Set(filteredItems.map((i) => i.id)));
  };

  const removeWi = async (id) => {
    if (!(await confirmAction("Supprimer cet ouvrage ?"))) return;
    await api.delete(`/work-items/${id}`);
    await loadAll();
    toast.success("Ouvrage supprimé");
  };

  const bulkDelete = async () => {
    if (selectedIds.size === 0) return;
    const n = selectedIds.size;
    const word = tab === "articles" ? "article" : "ouvrage";
    if (!(await confirmAction(`Supprimer ${n} ${word}${n > 1 ? "s" : ""} ?`))) return;
    await Promise.all(Array.from(selectedIds).map((id) => api.delete(`/work-items/${id}`)));
    setSelectedIds(new Set());
    await loadAll();
    toast.success(`${n} ${word}(s) supprimé(s)`);
  };

  const toggleSelectedMat = (id) => {
    const next = new Set(selectedMatIds);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    setSelectedMatIds(next);
  };

  const toggleSelectAllMats = () => {
    if (selectedMatIds.size === filteredMaterials.length) setSelectedMatIds(new Set());
    else setSelectedMatIds(new Set(filteredMaterials.map((m) => m.id)));
  };

  const bulkDeleteMats = async () => {
    if (selectedMatIds.size === 0) return;
    const n = selectedMatIds.size;
    if (!(await confirmAction(`Supprimer ${n} matériau${n > 1 ? "x" : ""} ?`))) return;
    await Promise.all(Array.from(selectedMatIds).map((id) => api.delete(`/materials/${id}`)));
    setSelectedMatIds(new Set());
    await loadAll();
    toast.success(`${n} matériau(x) supprimé(s)`);
  };

  const removeMat = async (id) => {
    if (!(await confirmAction("Supprimer ce matériau ?"))) return;
    await api.delete(`/materials/${id}`);
    await loadAll();
    toast.success("Matériau supprimé");
  };

  const convertMatToArticle = async (mat) => {
    if (!(await confirmAction(
      `Convertir « ${mat.name} » en article achat-revente ?\n\nUn nouvel article sera créé avec prix d'achat = ${mat.unit_price_ht}€/${mat.unit}. Le matériau d'origine est conservé.`,
      { confirmLabel: "Convertir" }
    ))) return;
    try {
      const { data } = await api.post(`/materials/${mat.id}/convert-to-article`);
      await loadAll();
      toast.success(`Article créé : ${data.code} (${data.description})`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur de conversion");
    }
  };

  const reseed = async (force = false) => {
    if (force && !(await confirmAction("Forcer la recharge : tous vos ouvrages et matières actuels seront effacés et remplacés par le catalogue Base de prix. Confirmer ?"))) return;
    try {
      const { data } = await api.post(`/library/reseed${force ? "?force=true" : ""}`);
      await loadAll();
      toast.success(`Bibliothèque rechargée : ${data.materials} matières, ${data.work_items} ouvrages`);
    } catch (e) {
      toast.error("Erreur lors du rechargement");
    }
  };

  return (
    <div data-testid="library-page" className="max-w-7xl mx-auto">
      <header className="mb-8 animate-in">
        <div className="flex items-center gap-2 font-body text-xs font-semibold mb-3" style={{ color: "#FFC400" }}>
          <BookOpen size={12} strokeWidth={2} /> Module 2 — Matériaux · Articles · MO
        </div>
        <div className="flex items-end justify-between gap-4 flex-wrap">
          <div>
            <h1 className="font-display text-[2.5rem] sm:text-5xl tracking-tight leading-[1.05]" style={{ fontWeight: 500 }}>
              Matériaux <span style={{ color: "var(--grey-soft)" }}>·</span> <span className="font-display" style={{ color: "#FFC400" }}>Articles</span> <span style={{ color: "var(--grey-soft)" }}>·</span> <span className="font-display" style={{ color: "#E10600" }}>MO</span>
            </h1>
            <p className="text-secondary-text mt-3 max-w-3xl font-body text-[15px]">
              Vos ouvrages et matériaux. Le coût unitaire est calculé en direct : <b>matières + (heures × point mort taux horaire)</b>.
              Taux actuel : <b>{fmtEUR(hourlyRate)}/h HT</b> · Marge par défaut : <b>{defaultMargin}%</b>
            </p>
          </div>
          <div className="flex gap-2 flex-wrap">
            {tab === "articles" && (
              <label className="btn btn-primary btn-sm cursor-pointer" data-testid="import-articles-csv-btn" title="Importez votre fichier Excel ou CSV : PitBoard reconnaît les colonnes automatiquement">
                <Download size={13} className="rotate-180" /> Importer articles (Excel / CSV)
                <input
                  type="file"
                  accept=".csv,.tsv,.txt,.xlsx,.xlsm,text/csv,text/tab-separated-values,text/plain,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                  className="hidden"
                  onChange={async (e) => {
                    const file = e.target.files?.[0];
                    e.target.value = "";
                    if (!file) return;
                    const fd = new FormData();
                    fd.append("file", file);
                    try {
                      const { data } = await api.post("/library/parse-table", fd, { headers: { "Content-Type": "multipart/form-data" } });
                      setImportWizard({ rows: data.rows, hasHeader: data.has_header });
                    } catch (err) {
                      toast.error(err?.response?.data?.detail || "Fichier illisible");
                    }
                  }}
                />
              </label>
            )}
            {tab === "ouvrages" && (
            <label className="btn btn-primary btn-sm cursor-pointer" data-testid="import-csv-btn" title="Importer un fichier CSV d'ouvrages métier">
              <Download size={13} className="rotate-180" /> Importer CSV
              <input
                type="file"
                accept=".csv,text/csv"
                className="hidden"
                onChange={async (e) => {
                  const file = e.target.files?.[0];
                  if (!file) return;
                  const fd = new FormData();
                  fd.append("file", file);
                  fd.append("mode", "append");
                  try {
                    const { data } = await api.post("/library/import-csv", fd, { headers: { "Content-Type": "multipart/form-data" } });
                    await loadAll();
                    const errMsg = data.errors?.length ? ` · ${data.errors.length} erreur(s)` : "";
                    toast.success(`Import : ${data.created} ouvrage(s) créé(s), ${data.skipped_duplicates} doublon(s) ignoré(s)${errMsg}`);
                    if (data.errors?.length) console.warn("CSV errors:", data.errors);
                  } catch (err) {
                    toast.error(err?.response?.data?.detail || "Échec import CSV");
                  } finally {
                    e.target.value = "";
                  }
                }}
              />
            </label>
            )}
            {tab === "ouvrages" && (
            <>
            <button onClick={() => reseed(false)} className="btn btn-outline btn-sm" data-testid="reseed-library" title="Ajouter les ouvrages Base de prix manquants (n'efface rien)">
              <Download size={13} /> Compléter Base de prix
            </button>
            <button onClick={() => reseed(true)} className="btn btn-ghost btn-sm" data-testid="reseed-library-force" title="Effacer mon catalogue et recharger Base de prix complet" style={{ color: "#E10600" }}>
              <Download size={13} /> Forcer rechargement complet
            </button>
            </>
            )}
          </div>
        </div>
      </header>

      {/* Tabs */}
      <div className="flex items-center justify-between gap-3 mb-6 flex-wrap">
        <div className="flex bg-[var(--surface)] border border-[var(--border)] rounded-lg p-1" data-testid="library-tabs">
          {[
            { id: "ouvrages", label: "Ouvrages (MO+matières)", icon: Wrench, count: ouvragesCount },
            { id: "materiaux", label: "Matériaux", icon: Package, count: materials.length },
            { id: "articles",  label: "Articles (achat-revente)", icon: Boxes, count: articlesCount },
          ].map(({ id, label, icon: Icon, count }) => (
            <button
              key={id}
              onClick={() => changeTab(id)}
              className={`btn btn-sm ${tab === id ? "btn-primary" : "btn-ghost"}`}
              data-testid={`tab-${id}`}
            >
              <Icon size={14} /> {label} <span className="opacity-60 ml-1">{count}</span>
            </button>
          ))}
        </div>

        <div className="flex items-center gap-2 flex-wrap">
          <div className="relative">
            <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-tertiary-text" />
            <input
              className="input pl-9 w-64"
              placeholder={tab === "materiaux" ? "Nom ou fournisseur…" : "Code ou description…"}
              value={q}
              onChange={(e) => setQ(e.target.value)}
              data-testid="library-search"
            />
          </div>
          <select className="input w-52" value={catFilter} onChange={(e) => setCatFilter(e.target.value)} data-testid="library-cat-filter">
            <option value="">Toutes catégories</option>
            {(tab === "materiaux" ? MAT_CATS : WORK_CATS).map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
          </select>
          {tab !== "materiaux" && (
            <button onClick={exportCsv} className="btn btn-outline btn-sm" data-testid="export-library">
              <Download size={14} /> Exporter CSV
            </button>
          )}
          <button
            onClick={() => {
              if (tab === "materiaux") setMatModal("new");
              else if (tab === "articles") setOpenWi("new-article");
              else setOpenWi("new");
            }}
            className="btn btn-accent"
            data-testid={tab === "materiaux" ? "add-material" : tab === "articles" ? "add-article" : "add-work-item"}
          >
            <Plus size={15} /> {tab === "materiaux" ? "Nouveau matériau" : tab === "articles" ? "Nouvel article" : "Nouvel ouvrage"}
          </button>
        </div>
      </div>

      {/* Bulk action bar */}
      {(tab === "ouvrages" || tab === "articles") && selectedIds.size > 0 && (
        <div className="card p-3 mb-4 flex items-center justify-between bg-[var(--carbon-3)] text-white" data-testid="bulk-action-bar">
          <div className="flex items-center gap-3 px-2">
            <span className="badge badge-accent">{selectedIds.size}</span>
            <span className="text-sm">{tab === "articles" ? "article" : "ouvrage"}{selectedIds.size > 1 ? "s" : ""} sélectionné{selectedIds.size > 1 ? "s" : ""}</span>
          </div>
          <div className="flex gap-2">
            <button onClick={() => setSelectedIds(new Set())} className="btn btn-ghost btn-sm hover:bg-[var(--surface)]/10">Désélectionner</button>
            <button onClick={bulkDelete} className="btn btn-sm bg-[#E10600] text-white hover:bg-red-700" data-testid="bulk-delete">
              <Trash2 size={13} /> Supprimer la sélection
            </button>
          </div>
        </div>
      )}
      {tab === "materiaux" && selectedMatIds.size > 0 && (
        <div className="card p-3 mb-4 flex items-center justify-between bg-[var(--carbon-3)] text-white" data-testid="bulk-action-bar-mats">
          <div className="flex items-center gap-3 px-2">
            <span className="badge badge-accent">{selectedMatIds.size}</span>
            <span className="text-sm">matériau{selectedMatIds.size > 1 ? "x" : ""} sélectionné{selectedMatIds.size > 1 ? "s" : ""}</span>
          </div>
          <div className="flex gap-2">
            <button onClick={() => setSelectedMatIds(new Set())} className="btn btn-ghost btn-sm hover:bg-[var(--surface)]/10">Désélectionner</button>
            <button onClick={bulkDeleteMats} className="btn btn-sm bg-[#E10600] text-white hover:bg-red-700" data-testid="bulk-delete-mats">
              <Trash2 size={13} /> Supprimer la sélection
            </button>
          </div>
        </div>
      )}

      {tab === "ouvrages" && (
        <WorkItemsTable
          items={filteredItems}
          selectedIds={selectedIds}
          onToggle={toggleSelected}
          onToggleAll={toggleSelectAll}
          onOpen={(it) => setOpenWi(it)}
          onDelete={removeWi}
        />
      )}

      {tab === "materiaux" && (
        <MaterialsTable
          items={filteredMaterials}
          selectedIds={selectedMatIds}
          onToggle={toggleSelectedMat}
          onToggleAll={toggleSelectAllMats}
          onEdit={(m) => setMatModal(m)}
          onDelete={removeMat}
          onConvertToArticle={convertMatToArticle}
        />
      )}

      {tab === "articles" && (
        <ArticlesTable
          items={filteredItems}
          selectedIds={selectedIds}
          onToggle={toggleSelected}
          onToggleAll={toggleSelectAll}
          onOpen={(it) => setOpenWi(it)}
          onStock={(it) => setStockEditor(it)}
          onDelete={removeWi}
        />
      )}

      {stockEditor && (
        <ArticleStockEditor
          item={stockEditor}
          onClose={() => setStockEditor(null)}
          onSaved={async () => { await loadAll(); setStockEditor(null); toast.success("Stock mis à jour"); }}
        />
      )}

      {openWi && (
        <WorkItemDrawer
          item={openWi === "new" || openWi === "new-article" ? null : openWi}
          initialKind={openWi === "new-article" ? "article" : "ouvrage"}
          materials={materials}
          hourlyRate={hourlyRate}
          defaultMargin={defaultMargin}
          onClose={() => setOpenWi(null)}
          onSaved={async () => { await loadAll(); setOpenWi(null); }}
        />
      )}

      {importWizard && (
        <ArticlesImportWizard
          data={importWizard}
          onClose={() => setImportWizard(null)}
          onDone={async () => { setImportWizard(null); await loadAll(); }}
        />
      )}

      <MaterialModalGate onLoad={loadAll} hourlyRate={hourlyRate} />
    </div>
  );
}

// ---------- Work item table ----------
function WorkItemsTable({ items, selectedIds, onToggle, onToggleAll, onOpen, onDelete }) {
  if (items.length === 0) {
    return (
      <div className="card p-16 text-center text-tertiary-text" data-testid="empty-work-items">
        <Wrench size={36} className="mx-auto mb-3 opacity-40" />
        <div className="font-medium text-primary-text mb-1">Aucun ouvrage</div>
        <div className="text-sm">Ajoutez votre premier ouvrage pour commencer à construire votre bibliothèque.</div>
      </div>
    );
  }
  const allSelected = items.length > 0 && items.every((it) => selectedIds.has(it.id));
  return (
    <div className="card overflow-hidden">
      <table className="table">
        <thead>
          <tr>
            <th className="w-10">
              <input type="checkbox" checked={allSelected} onChange={onToggleAll} className="cursor-pointer" data-testid="select-all" />
            </th>
            <th>Code</th>
            <th>Description</th>
            <th>Catégorie</th>
            <th className="text-center">Unité</th>
            <th className="text-right">Temps MO</th>
            <th className="text-right">Coût HT</th>
            <th className="text-right">Marge</th>
            <th className="text-right">Prix vente HT</th>
            <th className="w-12"></th>
          </tr>
        </thead>
        <tbody>
          {items.map((it) => {
            const isSel = selectedIds.has(it.id);
            return (
              <tr key={it.id} className={`cursor-pointer ${isSel ? "bg-[rgba(255,196,0,0.10)]" : ""}`} onClick={() => onOpen(it)} data-testid={`wi-${it.id}`}>
                <td onClick={(e) => e.stopPropagation()}>
                  <input type="checkbox" checked={isSel} onChange={() => onToggle(it.id)} className="cursor-pointer" data-testid={`select-${it.id}`} />
                </td>
                <td className="font-mono text-xs text-[var(--pit-yellow)]">{it.code}</td>
                <td>
                  <div className="font-medium flex items-center gap-2">
                    {it.kind === "article" && <span className="badge badge-accent" style={{ fontSize: "0.65rem", padding: "0.1rem 0.45rem" }}>Article</span>}
                    {it.description}
                  </div>
                  <div className="text-xs text-[var(--grey)]">{it.kind === "article" ? "Achat-revente" : `${(it.materials || []).length} matériaux · ${(it.tools || []).length} outils`}</div>
                </td>
                <td><span className="badge badge-muted">{findLabel(WORK_CATS, it.category)}</span></td>
                <td className="text-center text-[var(--ink-soft)]">/ {it.unit}</td>
                <td className="text-right">{it.kind === "article" ? "—" : `${Math.round((it.labor_hours_per_unit || 0) * 60)} min`}</td>
                <td className="text-right font-mono">{fmtEUR(it.unit_cost_ht)}</td>
                <td className="text-right font-mono text-[#FFC400]">{it.effective_margin_pct}%</td>
                <td className="text-right font-mono font-semibold">{fmtEUR(it.sell_price_ht)}</td>
                <td className="text-right">
                  <button
                    onClick={(e) => { e.stopPropagation(); onDelete(it.id); }}
                    className="w-8 h-8 rounded-md border border-[var(--border)] text-[#E10600] hover:bg-[rgba(225,6,0,0.12)] hover:border-[#E10600] inline-flex items-center justify-center transition-colors"
                    title="Supprimer l'ouvrage"
                    data-testid={`wi-delete-${it.id}`}
                  >
                    <Trash2 size={13} />
                  </button>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

// ---------- Materials table ----------
function MaterialsTable({ items, selectedIds, onToggle, onToggleAll, onEdit, onDelete, onConvertToArticle }) {
  if (items.length === 0) {
    return (
      <div className="card p-16 text-center text-tertiary-text" data-testid="empty-materials">
        <Package size={36} className="mx-auto mb-3 opacity-40" />
        <div className="font-medium text-primary-text mb-1">Aucun matériau</div>
        <div className="text-sm">Créez votre catalogue de matières premières (peintures, colles, etc.).</div>
      </div>
    );
  }
  const allSelected = items.length > 0 && items.every((m) => selectedIds.has(m.id));
  return (
    <div className="card overflow-hidden">
      <table className="table">
        <thead>
          <tr>
            <th className="w-10">
              <input type="checkbox" checked={allSelected} onChange={onToggleAll} className="cursor-pointer" data-testid="select-all-mats" />
            </th>
            <th>Matériau</th>
            <th>Catégorie</th>
            <th>Fournisseur</th>
            <th className="text-center">Unité</th>
            <th className="text-right">Prix HT</th>
            <th className="w-24"></th>
          </tr>
        </thead>
        <tbody>
          {items.map((m) => (
            <tr key={m.id} className={`cursor-pointer ${selectedIds.has(m.id) ? "bg-[rgba(255,196,0,0.10)]" : ""}`} onClick={() => onEdit(m)} data-testid={`mat-${m.id}`}>
              <td onClick={(e) => e.stopPropagation()}>
                <input type="checkbox" checked={selectedIds.has(m.id)} onChange={() => onToggle(m.id)} className="cursor-pointer" data-testid={`select-mat-${m.id}`} />
              </td>
              <td className="font-medium">{m.name}</td>
              <td><span className="badge badge-muted">{findLabel(MAT_CATS, m.category)}</span></td>
              <td className="text-secondary-text">{m.supplier || "—"}</td>
              <td className="text-center font-mono">{m.unit}</td>
              <td className="text-right font-mono">{fmtEUR(m.unit_price_ht)} / {m.unit}</td>
              <td className="text-right">
                <div className="inline-flex items-center gap-1">
                  <button
                    onClick={(e) => { e.stopPropagation(); onConvertToArticle?.(m); }}
                    className="w-8 h-8 rounded-md border border-[var(--border)] text-[#FFC400] hover:bg-[rgba(255,196,0,0.12)] hover:border-[#FFC400] inline-flex items-center justify-center transition-colors"
                    title="Convertir en article achat-revente"
                    data-testid={`mat-convert-${m.id}`}
                  >
                    <Boxes size={13} />
                  </button>
                  <button
                    onClick={(e) => { e.stopPropagation(); onDelete(m.id); }}
                    className="w-8 h-8 rounded-md border border-[var(--border)] text-[#E10600] hover:bg-[rgba(225,6,0,0.12)] hover:border-[#E10600] inline-flex items-center justify-center transition-colors"
                    title="Supprimer le matériau"
                    data-testid={`mat-delete-${m.id}`}
                  >
                    <Trash2 size={13} />
                  </button>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ---------- Articles table (kind="article") ----------
function ArticlesTable({ items, selectedIds, onToggle, onToggleAll, onOpen, onStock, onDelete }) {
  if (items.length === 0) {
    return (
      <div className="card text-center py-16" data-testid="articles-empty">
        <Boxes size={42} className="mx-auto mb-4 text-tertiary-text opacity-50" strokeWidth={1.5} />
        <div className="font-display text-xl mb-2" style={{ fontWeight: 600 }}>Aucun article</div>
        <div className="text-sm text-secondary-text max-w-md mx-auto">
          Créez votre premier article achat-revente (sans main d'œuvre). Vous pourrez ensuite gérer son stock en un clic.
        </div>
      </div>
    );
  }
  return (
    <div className="card overflow-hidden">
      <table className="table">
        <thead>
          <tr>
            <th className="w-10">
              <input type="checkbox" checked={items.length > 0 && items.every((it) => selectedIds.has(it.id))} onChange={onToggleAll} className="cursor-pointer" data-testid="select-all-articles" />
            </th>
            <th>Code</th>
            <th>Article</th>
            <th>Catégorie</th>
            <th className="text-right">PA HT</th>
            <th className="text-right">Marge</th>
            <th className="text-right">PV HT</th>
            <th className="text-right">Stock</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          {items.map((it) => {
            const qty = Number(it.stock_qty || 0);
            const threshold = Number(it.stock_threshold || 0);
            const isLow = threshold > 0 && qty <= threshold;
            const marginEur = (Number(it.sell_price_ht) || 0) - (Number(it.purchase_price_ht) || 0);
            return (
              <tr key={it.id} data-testid={`article-row-${it.id}`} className={`cursor-pointer hover:bg-[var(--carbon-3)]/40 ${selectedIds.has(it.id) ? "bg-[rgba(255,196,0,0.10)]" : ""}`} onClick={() => onOpen(it)}>
                <td onClick={(e) => e.stopPropagation()}>
                  <input type="checkbox" checked={selectedIds.has(it.id)} onChange={() => onToggle(it.id)} className="cursor-pointer" data-testid={`select-article-${it.id}`} />
                </td>
                <td className="font-mono text-xs" style={{ color: "#FFC400" }}>{it.code}</td>
                <td>
                  <div className="font-medium">{it.description}</div>
                  {it.supplier && <div className="text-xs text-[var(--grey)]">Fourn. : {it.supplier}</div>}
                </td>
                <td><span className="badge badge-muted">{findLabel(WORK_CATS, it.category)}</span></td>
                <td className="text-right">{fmtEUR(it.purchase_price_ht)}</td>
                <td className="text-right" style={{ color: "#FFC400" }}>
                  {fmtEUR(marginEur)}
                  <span className="text-[11px] text-[var(--grey)] ml-1">({it.effective_margin_pct}%)</span>
                </td>
                <td className="text-right font-semibold">{fmtEUR(it.sell_price_ht)}</td>
                <td className="text-right">
                  <span className="numerals font-semibold" style={{ color: isLow ? "#E10600" : "var(--chalk)" }}>
                    {qty}
                  </span>
                  <span className="text-[11px] text-[var(--grey)] ml-1">{it.unit}</span>
                  {isLow && <span className="badge badge-danger ml-2" style={{ fontSize: "0.65rem", padding: "0.1rem 0.4rem" }}>Rupture</span>}
                </td>
                <td className="text-right whitespace-nowrap" onClick={(e) => e.stopPropagation()}>
                  <button
                    onClick={() => onStock(it)}
                    className="inline-flex items-center gap-1.5 px-2.5 h-8 rounded-md border border-[var(--border)] hover:border-[#FFC400] hover:text-[#FFC400] text-xs font-medium transition-colors mr-1"
                    title="Gérer le stock"
                    data-testid={`article-stock-${it.id}`}
                  >
                    <Boxes size={12} /> Stock
                  </button>
                  <button
                    onClick={() => onDelete(it.id)}
                    className="w-8 h-8 rounded-md border border-[var(--border)] text-[#E10600] hover:bg-[rgba(225,6,0,0.12)] hover:border-[#E10600] inline-flex items-center justify-center"
                    title="Supprimer"
                  >
                    <Trash2 size={13} />
                  </button>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

// ---------- Article Stock quick editor (modal léger qty/seuil/fournisseur) ----------
function ArticleStockEditor({ item, onClose, onSaved }) {
  const [qty, setQty] = useState(Number(item.stock_qty || 0));
  const [threshold, setThreshold] = useState(Number(item.stock_threshold || 0));
  const [supplier, setSupplier] = useState(item.supplier || "");
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      await api.patch(`/work-items/${item.id}`, {
        code: item.code,
        description: item.description,
        category: item.category,
        unit: item.unit,
        labor_hours_per_unit: 0,
        materials: [],
        tools: [],
        margin_pct: item.margin_pct,
        notes: item.notes || "",
        kind: "article",
        purchase_price_ht: Number(item.purchase_price_ht) || 0,
        stock_qty: Number(qty) || 0,
        stock_threshold: Number(threshold) || 0,
        supplier: supplier || "",
      });
      await onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}>
      <div className="card max-w-md w-full" onClick={(e) => e.stopPropagation()} data-testid="article-stock-editor">
        <div className="flex items-center justify-between mb-4 pb-3 border-b border-[var(--border)]">
          <div>
            <div className="text-xs font-semibold" style={{ color: "#FFC400" }}>Article · {item.code}</div>
            <h3 className="font-display text-xl mt-0.5">{item.description}</h3>
          </div>
          <button onClick={onClose} className="btn btn-ghost btn-sm"><X size={14} /></button>
        </div>
        <div className="space-y-4">
          <div>
            <label className="label">Quantité en stock ({item.unit})</label>
            <input type="number" step="1" className="input text-right" value={qty} onChange={(e) => setQty(e.target.value)} data-testid="article-qty-input" />
          </div>
          <div>
            <label className="label">Seuil d'alerte rupture ({item.unit})</label>
            <input type="number" step="1" className="input text-right" value={threshold} onChange={(e) => setThreshold(e.target.value)} data-testid="article-threshold-input" />
            <div className="text-xs text-[var(--grey)] mt-1">Alerte rouge si stock ≤ seuil. Mettre 0 pour désactiver.</div>
          </div>
          <div>
            <label className="label">Fournisseur principal</label>
            <input className="input" value={supplier} onChange={(e) => setSupplier(e.target.value)} placeholder="Ex : Castorama, Point P…" />
          </div>
        </div>
        <div className="flex justify-end gap-2 mt-6 pt-4 border-t border-[var(--border)]">
          <button onClick={onClose} className="btn btn-outline">Annuler</button>
          <button onClick={save} disabled={saving} className="btn btn-primary" data-testid="article-stock-save">
            <Save size={14} /> {saving ? "..." : "Enregistrer"}
          </button>
        </div>
      </div>
    </div>
  );
}


// ---------- Work item drawer ----------
function WorkItemDrawer({ item, initialKind = "ouvrage", materials, hourlyRate, defaultMargin, onClose, onSaved }) {
  const blank = {
    code: "",
    description: "",
    category: "peinture",
    unit: "m²",
    labor_hours_per_unit: 0,
    materials: [],
    tools: [],
    margin_pct: null,
    notes: "",
    kind: initialKind,
    purchase_price_ht: 0,
  };
  const [form, setForm] = useState(item ? { ...item, tools: item.tools || [], materials: item.materials || [], kind: item.kind || "ouvrage", purchase_price_ht: item.purchase_price_ht ?? 0 } : blank);
  const [saving, setSaving] = useState(false);
  const [newTool, setNewTool] = useState("");
  const isArticle = form.kind === "article";

  // Live computed values (ouvrage = matières+MO ; article = prix achat)
  const matCost = isArticle
    ? (Number(form.purchase_price_ht) || 0)
    : form.materials.reduce((s, m) => s + (Number(m.quantity) || 0) * (Number(m.unit_price_ht) || 0), 0);
  const laborCost = isArticle ? 0 : (Number(form.labor_hours_per_unit) || 0) * hourlyRate;
  const unitCost = matCost + laborCost;
  const margin = form.margin_pct === null || form.margin_pct === "" ? defaultMargin : Number(form.margin_pct);
  const sellPrice = unitCost * (1 + margin / 100);

  const addMaterialLine = () => {
    setForm({ ...form, materials: [...form.materials, { material_id: null, name: "", quantity: 0, unit: "L", unit_price_ht: 0 }] });
  };
  const updateLine = (idx, patch) => {
    const copy = [...form.materials];
    copy[idx] = { ...copy[idx], ...patch };
    setForm({ ...form, materials: copy });
  };
  const removeLine = (idx) => {
    setForm({ ...form, materials: form.materials.filter((_, i) => i !== idx) });
  };
  const pickFromCatalog = (idx, matId) => {
    const m = materials.find((x) => x.id === matId);
    if (!m) return updateLine(idx, { material_id: null });
    updateLine(idx, { material_id: m.id, name: m.name, unit: m.unit, unit_price_ht: m.unit_price_ht });
  };

  const save = async () => {
    if (!form.code.trim() || !form.description.trim()) {
      toast.error("Code et description obligatoires");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        code: form.code.trim(),
        description: form.description.trim(),
        category: form.category,
        unit: form.unit,
        labor_hours_per_unit: isArticle ? 0 : (Number(form.labor_hours_per_unit) || 0),
        materials: isArticle ? [] : form.materials.map((m) => ({
          material_id: m.material_id || null,
          name: m.name,
          quantity: Number(m.quantity) || 0,
          unit: m.unit,
          unit_price_ht: Number(m.unit_price_ht) || 0,
        })),
        tools: isArticle ? [] : form.tools,
        margin_pct: form.margin_pct === null || form.margin_pct === "" ? null : Number(form.margin_pct),
        notes: form.notes || "",
        kind: form.kind || "ouvrage",
        purchase_price_ht: isArticle ? (Number(form.purchase_price_ht) || 0) : 0,
      };
      if (item) await api.patch(`/work-items/${item.id}`, payload);
      else await api.post("/work-items", payload);
      const itemLabel = isArticle ? "Article" : "Ouvrage";
      toast.success(item ? `${itemLabel} mis à jour` : `${itemLabel} créé`);
      await onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[9999] flex" data-testid="wi-drawer">
      <div className="flex-1 bg-black/30 backdrop-blur-sm" onClick={onClose} />
      <aside className="w-full max-w-2xl bg-app h-full overflow-y-auto slide-in flex flex-col">
        {/* Header */}
        <div className="px-7 pt-6 pb-5 bg-[var(--carbon-3)] text-white relative">
          <button onClick={onClose} className="absolute top-4 right-4 w-9 h-9 rounded-full bg-[var(--surface)]/10 hover:bg-[var(--surface)]/20 flex items-center justify-center" data-testid="wi-close">
            <X size={16} />
          </button>
          <p className="text-[11px] font-semibold text-[#FFC400] mb-2">
            {item ? "Modifier l'ouvrage" : "Nouvel ouvrage"}
          </p>
          <h2 className="font-display text-3xl leading-tight pr-10">{form.description || "Sans titre"}</h2>
          <div className="flex items-center gap-3 mt-3 text-xs text-[var(--ink-soft)] flex-wrap">
            <span className="font-mono text-[#FFC400]">{form.code || "—"}</span>
            <span>·</span>
            <span>{findLabel(WORK_CATS, form.category)}</span>
            <span>·</span>
            <span>par {form.unit}</span>
          </div>
        </div>

        {/* Live KPI strip — fond surface lisible.
            Article : 3 cases (Prix achat / Marge € / Prix vente)
            Ouvrage : 4 cases (Coût matières / Coût MO / Marge € / Prix vente) */}
        <div className={`grid gap-3 px-7 py-5 bg-[var(--surface)] border-b border-[var(--border)] sticky top-0 z-10 ${isArticle ? "grid-cols-3" : "grid-cols-4"}`} data-testid="wi-live-kpi">
          {(isArticle ? [
            { label: "Prix d'achat HT", v: matCost, c: "#FFC400" },
            { label: "Marge", v: sellPrice - unitCost, c: "#FFC400", hint: `${margin}%` },
            { label: "Prix vente HT", v: sellPrice, c: "#E10600" },
          ] : [
            { label: "Coût matières", v: matCost, c: "#FFC400" },
            { label: "Coût MO", v: laborCost, c: "#FFC400", hint: `${Math.round((Number(form.labor_hours_per_unit) || 0) * 60)} min × ${fmtEUR(hourlyRate)}/h` },
            { label: "Marge", v: sellPrice - unitCost, c: "#FFC400", hint: `${margin}%` },
            { label: "Prix vente HT", v: sellPrice, c: "#E10600" },
          ]).map(({ label, v, c, hint }) => (
            <div key={label} className="rounded-lg border border-[var(--border)] bg-[var(--carbon-2)] p-3 text-center" style={{ boxShadow: `inset 0 3px 0 0 ${c}` }}>
              <div className="text-[11px] font-semibold text-[var(--ink-soft)]">{label}</div>
              <div className="numerals text-xl text-[var(--chalk)] mt-1 leading-none">{fmtEUR(v)}</div>
              {hint && <div className="text-[11px] text-[var(--grey)] mt-1.5">{hint}</div>}
            </div>
          ))}
        </div>

        {/* Form body */}
        <div className="flex-1 px-7 pt-5 pb-6 space-y-6">
          {/* Type toggle Ouvrage / Article */}
          <section>
            <label className="label">Type</label>
            <div className="flex gap-2" data-testid="wi-kind-toggle">
              {[
                { k: "ouvrage", label: "Ouvrage (MO + matières)", desc: "Main d'œuvre + matières premières" },
                { k: "article", label: "Article (achat-revente)", desc: "Marchandise revendue avec marge, sans MO" },
              ].map(({ k, label, desc }) => {
                const active = (form.kind || "ouvrage") === k;
                return (
                  <button
                    key={k}
                    type="button"
                    onClick={() => setForm({ ...form, kind: k })}
                    className="flex-1 text-left rounded-lg border p-3 transition-colors"
                    style={{
                      borderColor: active ? "#FFC400" : "var(--border)",
                      background: active ? "rgba(255,196,0,0.08)" : "transparent",
                      color: "var(--chalk)",
                    }}
                    data-testid={`wi-kind-${k}`}
                  >
                    <div className="font-body text-sm font-semibold">{label}</div>
                    <div className="text-[12px] text-[var(--grey)] mt-0.5">{desc}</div>
                  </button>
                );
              })}
            </div>
          </section>

          <section>
            <h3 className="font-semibold text-sm text-[var(--ink-soft)] mb-3">Identité</h3>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="label">Code (référence)</label>
                <input className="input" placeholder={isArticle ? "ART-001" : "BAT-PNT-001"} value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })} data-testid="wi-code" />
              </div>
              <div>
                <label className="label">Catégorie</label>
                <select className="input" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} data-testid="wi-category">
                  {WORK_CATS.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
                </select>
              </div>
              <div className="col-span-2">
                <label className="label">Description</label>
                <input className="input" placeholder={isArticle ? "Ex : Robinet mitigeur Grohe" : "Pose toile de verre + peinture finition 2 couches"}
                  value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="wi-desc" />
              </div>
              <div>
                <label className="label">Unité</label>
                <select className="input" value={form.unit} onChange={(e) => setForm({ ...form, unit: e.target.value })} data-testid="wi-unit">
                  {UNITS.map((u) => <option key={u} value={u}>{u}</option>)}
                </select>
              </div>
              {isArticle ? (
                <div>
                  <label className="label">Prix d'achat HT / unité</label>
                  <input type="number" step="0.01" className="input text-right"
                    value={form.purchase_price_ht || ""}
                    onChange={(e) => setForm({ ...form, purchase_price_ht: e.target.value })}
                    data-testid="wi-purchase-price" />
                </div>
              ) : (
                <div>
                  <label className="label">Temps MO / unité (minutes)</label>
                  <input type="number" step="1" min="0" className="input"
                    value={form.labor_hours_per_unit ? Math.round(Number(form.labor_hours_per_unit) * 60) : ""}
                    onChange={(e) => {
                      const minutes = Number(e.target.value) || 0;
                      setForm({ ...form, labor_hours_per_unit: minutes / 60 });
                    }} data-testid="wi-hours" />
                </div>
              )}
              <div className="col-span-2">
                <label className="label">Marge (%, vide = défaut {defaultMargin}%)</label>
                <input type="number" step="1" className="input"
                  value={form.margin_pct ?? ""}
                  placeholder={`${defaultMargin}`}
                  onChange={(e) => setForm({ ...form, margin_pct: e.target.value === "" ? null : e.target.value })}
                  data-testid="wi-margin" />
              </div>
            </div>
          </section>

          {!isArticle && (
          <section>
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-semibold text-sm text-[var(--ink-soft)]">Composition matières / unité d'ouvrage</h3>
              <button onClick={addMaterialLine} className="btn btn-outline btn-sm" data-testid="wi-add-line">
                <Plus size={13} /> Ajouter une ligne
              </button>
            </div>
            {form.materials.length === 0 && (
              <div className="text-center text-[var(--grey)] text-sm py-6 border-2 border-dashed border-[var(--border)] rounded-lg">
                Aucune ligne. Ajoutez les matériaux consommés par unité d'ouvrage.
              </div>
            )}
            {form.materials.length > 0 && (
              <div className="grid grid-cols-12 gap-2 mb-2 px-1 pb-2 border-b border-[var(--border)]">
                <div className="col-span-5 text-[11px] text-[var(--ink-soft)] font-semibold">Désignation matière</div>
                <div className="col-span-2 text-[11px] text-[var(--ink-soft)] font-semibold text-right">Quantité</div>
                <div className="col-span-2 text-[11px] text-[var(--ink-soft)] font-semibold">Unité</div>
                <div className="col-span-2 text-[11px] text-[var(--ink-soft)] font-semibold text-right">PU HT</div>
                <div className="col-span-1 text-[11px] text-[var(--ink-soft)] font-semibold text-right">Suppr.</div>
              </div>
            )}
            {form.materials.map((m, idx) => (
              <div key={idx} className="grid grid-cols-12 gap-2 mb-2 items-center" data-testid={`wi-line-${idx}`}>
                <div className="col-span-5">
                  <select
                    className="input"
                    value={m.material_id || ""}
                    onChange={(e) => pickFromCatalog(idx, e.target.value)}
                  >
                    <option value="">— Libre / hors catalogue —</option>
                    {materials.map((mat) => <option key={mat.id} value={mat.id}>{mat.name}</option>)}
                  </select>
                  {!m.material_id && (
                    <input className="input mt-1.5" placeholder="Nom libre" value={m.name} onChange={(e) => updateLine(idx, { name: e.target.value })} />
                  )}
                </div>
                <div className="col-span-2">
                  <input type="number" step="0.001" className="input text-right font-mono" placeholder="0"
                    value={m.quantity} onChange={(e) => updateLine(idx, { quantity: e.target.value })} />
                </div>
                <div className="col-span-2">
                  <select className="input" value={m.unit} onChange={(e) => updateLine(idx, { unit: e.target.value })} disabled={!!m.material_id}>
                    {MAT_UNITS.map((u) => <option key={u} value={u}>{u}</option>)}
                  </select>
                </div>
                <div className="col-span-2">
                  <input type="number" step="0.01" className="input text-right font-mono" placeholder="0,00"
                    value={m.unit_price_ht}
                    onChange={(e) => updateLine(idx, { unit_price_ht: e.target.value })} disabled={!!m.material_id} />
                </div>
                <div className="col-span-1 text-right">
                  <button
                    onClick={() => removeLine(idx)}
                    className="w-9 h-9 rounded-md border border-[var(--border)] text-[#E10600] hover:bg-[rgba(225,6,0,0.12)] hover:border-[#E10600] inline-flex items-center justify-center transition-colors"
                    title="Supprimer cette ligne"
                    data-testid={`wi-line-delete-${idx}`}
                  >
                    <Trash2 size={14} />
                  </button>
                </div>
                <div className="col-span-12 text-right text-xs text-[var(--grey)] -mt-1">
                  Sous-total ligne = {fmtEUR((Number(m.quantity) || 0) * (Number(m.unit_price_ht) || 0))}
                </div>
              </div>
            ))}
          </section>
          )}

          {!isArticle && (
          <section>
            <h3 className="font-semibold text-sm text-[var(--ink-soft)] mb-3">Outillage requis</h3>
            <div className="flex flex-wrap gap-2 mb-3">
              {(form.tools || []).map((t, i) => (
                <span key={i} className="badge badge-primary inline-flex items-center gap-1.5">
                  <Wrench size={11} /> {t}
                  <button onClick={() => setForm({ ...form, tools: form.tools.filter((_, j) => j !== i) })} className="hover:opacity-80">
                    <X size={11} />
                  </button>
                </span>
              ))}
            </div>
            <div className="flex gap-2">
              <input className="input" placeholder="Ex: rouleau 250mm" value={newTool} onChange={(e) => setNewTool(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter" && newTool.trim()) { setForm({ ...form, tools: [...(form.tools || []), newTool.trim()] }); setNewTool(""); } }} />
              <button onClick={() => { if (newTool.trim()) { setForm({ ...form, tools: [...(form.tools || []), newTool.trim()] }); setNewTool(""); } }} className="btn btn-outline">
                <Plus size={14} />
              </button>
            </div>
          </section>
          )}

          <section>
            <label className="label">Notes</label>
            <textarea rows={3} className="input" placeholder="Particularités, conditions, pertes..."
              value={form.notes || ""} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
          </section>
        </div>

        {/* Sticky save bar — boutons à gauche pour éviter le watermark Emergent (bottom-right) */}
        <div className="sticky bottom-0 bg-[var(--surface)] border-t border-[var(--border)] px-7 py-4 flex justify-start gap-2">
          <button onClick={save} disabled={saving} className="btn btn-primary" data-testid="wi-save">
            <Save size={14} /> {saving ? "Enregistrement..." : "Enregistrer"}
          </button>
          <button onClick={onClose} className="btn btn-outline">Annuler</button>
        </div>
      </aside>
    </div>
  );
}

// ---------- Material modal (simpler) ----------
let __setMatModal = () => {};
function MaterialModalGate({ onLoad }) {
  const [m, setM] = useState(null);
  __setMatModal = setM;
  if (!m) return null;
  return <MaterialModal item={m === "new" ? null : m} onClose={() => setM(null)} onSaved={async () => { await onLoad(); setM(null); }} />;
}
const setMatModal = (v) => __setMatModal(v);

function MaterialModal({ item, onClose, onSaved }) {
  const [form, setForm] = useState(item || { name: "", category: "peinture", unit: "L", unit_price_ht: 0, supplier: "", notes: "" });
  const [saving, setSaving] = useState(false);

  const save = async () => {
    if (!form.name.trim()) { toast.error("Nom obligatoire"); return; }
    setSaving(true);
    try {
      const payload = { ...form, unit_price_ht: Number(form.unit_price_ht) || 0 };
      if (item) await api.patch(`/materials/${item.id}`, payload);
      else await api.post("/materials", payload);
      toast.success(item ? "Matériau mis à jour" : "Matériau créé");
      await onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={onClose}>
      <div className="card max-w-lg w-full" onClick={(e) => e.stopPropagation()} data-testid="mat-modal">
        <div className="px-6 py-4 border-b border-[var(--border)] flex items-center justify-between">
          <h3 className="font-semibold text-lg">{item ? "Modifier le matériau" : "Nouveau matériau"}</h3>
          <button onClick={onClose} className="btn btn-ghost btn-sm"><X size={14} /></button>
        </div>
        <div className="p-6 space-y-4">
          <div>
            <label className="label">Nom du matériau</label>
            <input className="input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="mat-name" />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="label">Catégorie</label>
              <select className="input" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} data-testid="mat-category">
                {MAT_CATS.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
              </select>
            </div>
            <div>
              <label className="label">Unité</label>
              <select className="input" value={form.unit} onChange={(e) => setForm({ ...form, unit: e.target.value })}>
                {MAT_UNITS.map((u) => <option key={u} value={u}>{u}</option>)}
              </select>
            </div>
            <div>
              <label className="label">Prix HT / unité</label>
              <input type="number" step="0.01" className="input text-right font-mono"
                value={form.unit_price_ht} onChange={(e) => setForm({ ...form, unit_price_ht: e.target.value })} data-testid="mat-price" />
            </div>
            <div>
              <label className="label">Fournisseur</label>
              <input className="input" value={form.supplier || ""} onChange={(e) => setForm({ ...form, supplier: e.target.value })} />
            </div>
          </div>
        </div>
        <div className="px-6 py-4 border-t border-[var(--border)] flex justify-start gap-2">
          <button onClick={save} disabled={saving} className="btn btn-primary" data-testid="mat-save">
            <Save size={14} /> {saving ? "..." : "Enregistrer"}
          </button>
          <button onClick={onClose} className="btn btn-outline">Annuler</button>
        </div>
      </div>
    </div>
  );
}

// ---------- Import articles : assistant de mapping (parsing côté serveur) ----------
const IMPORT_FIELDS = [
  { v: "ignore", label: "— Ignorer —" },
  { v: "description", label: "Description (nom)" },
  { v: "description_extra", label: "Complément description" },
  { v: "code", label: "Code / Référence" },
  { v: "purchase_price_ht", label: "Prix d'achat HT" },
  { v: "sell_price_ht", label: "Prix de vente HT" },
  { v: "margin_pct", label: "Marge %" },
  { v: "unit", label: "Unité" },
  { v: "category", label: "Catégorie" },
  { v: "supplier", label: "Fournisseur" },
  { v: "stock_qty", label: "Stock" },
  { v: "stock_threshold", label: "Seuil alerte stock" },
];

function guessMapping(rows, hasHeader) {
  const nCols = Math.max(...rows.map((r) => r.length));
  const mapping = {};
  // Ordre de priorité : code avant description (évite « Réf. Article » → description)
  const ORDERED_ALIASES = [
    ["code", ["réf", "ref", "reference", "référence", "code", "sku"]],
    ["purchase_price_ht", ["prix_achat", "prix achat", "pa ht", "pa_ht", "achat"]],
    ["sell_price_ht", ["prix_vente", "prix vente", "pv ht", "pv_ht", "vente", "prix public", "tarif", "prix ht"]],
    ["margin_pct", ["marge", "margin"]],
    ["unit", ["unite", "unité", "unit"]],
    ["category", ["categorie", "catégorie", "category", "famille", "gamme"]],
    ["supplier", ["fournisseur", "supplier", "marque"]],
    ["stock_qty", ["stock", "qte", "quantite", "quantité"]],
    ["stock_threshold", ["seuil", "stock_min"]],
    ["description", ["description", "designation", "désignation", "nom", "libelle", "libellé", "modele", "modèle", "article", "name", "produit"]],
    ["description_extra", ["puissance", "finition", "habillage", "option", "detail", "détail", "commentaire"]],
  ];
  if (hasHeader) {
    const used = new Set();
    rows[0].forEach((h, i) => {
      const hn = (h || "").toLowerCase().trim();
      if (!hn) return;
      for (const [field, aliases] of ORDERED_ALIASES) {
        if (field !== "description_extra" && used.has(field)) continue;
        if (field === "sell_price_ht" && hn.includes("ttc")) continue;
        if (aliases.some((a) => hn === a || hn.includes(a))) {
          mapping[i] = field;
          used.add(field);
          break;
        }
      }
    });
  }
  if (!hasHeader || Object.keys(mapping).length === 0) {
    const dataRows = rows.slice(hasHeader ? 1 : 0, (hasHeader ? 1 : 0) + 20);
    if (!dataRows.length) return mapping;
    const isPriceCol = (i) => dataRows.filter((r) => /\d/.test(r[i] || "") && /[€]|\d[.,]\d{2}\s*€?\s*$/.test((r[i] || "").trim())).length >= dataRows.length / 2;
    const priceCols = [];
    for (let i = 0; i < nCols; i++) if (isPriceCol(i)) priceCols.push(i);
    if (priceCols.length >= 1) mapping[priceCols[0]] = "purchase_price_ht";
    if (priceCols.length >= 2) mapping[priceCols[1]] = "sell_price_ht";
    let best = -1, bestLen = 0;
    for (let i = 0; i < nCols; i++) {
      if (mapping[i]) continue;
      const avg = dataRows.reduce((s, r) => s + ((r[i] || "").length), 0) / dataRows.length;
      if (avg > bestLen) { bestLen = avg; best = i; }
    }
    if (best >= 0) mapping[best] = "description";
  }
  return mapping;
}

function ArticlesImportWizard({ data, onClose, onDone }) {
  const [hasHeader, setHasHeader] = useState(data.hasHeader);
  const [mapping, setMapping] = useState(() => guessMapping(data.rows, data.hasHeader));
  const [busy, setBusy] = useState(false);
  const [importErrors, setImportErrors] = useState(null);
  const nCols = Math.max(...data.rows.map((r) => r.length));
  const preview = data.rows.slice(hasHeader ? 1 : 0, (hasHeader ? 1 : 0) + 5);
  const totalRows = data.rows.length - (hasHeader ? 1 : 0);

  const canImport = Object.values(mapping).includes("description") &&
    (Object.values(mapping).includes("purchase_price_ht") || Object.values(mapping).includes("sell_price_ht"));
  const [advanced, setAdvanced] = useState(!canImport);

  const setCol = (i, v) => {
    const next = { ...mapping };
    if (v === "ignore") delete next[i];
    else {
      Object.keys(next).forEach((k) => { if (next[k] === v && !v.startsWith("description")) delete next[k]; });
      next[i] = v;
    }
    setMapping(next);
  };

  const fieldLabel = (v) => IMPORT_FIELDS.find((f) => f.v === v)?.label || v;
  const colsFor = (field) => Object.entries(mapping).filter(([, v]) => v === field).map(([k]) => +k);
  const colName = (i) => (hasHeader && data.rows[0]?.[i]) ? `« ${data.rows[0][i]} »` : `colonne ${i + 1}`;
  const detected = ["description", "description_extra", "code", "purchase_price_ht", "sell_price_ht", "margin_pct", "category", "supplier", "unit", "stock_qty", "stock_threshold"]
    .map((f) => ({ f, cols: colsFor(f) })).filter((d) => d.cols.length);
  const simplePreview = preview.slice(0, 3).map((r) => {
    const join = (f, s = " ") => colsFor(f).map((i) => r[i]).filter(Boolean).join(s);
    const extra = join("description_extra", " · ");
    const desc = join("description");
    return {
      desc: extra ? `${desc} — ${extra}` : desc,
      code: join("code"),
      price: join("sell_price_ht") || join("purchase_price_ht"),
    };
  });

  const doImport = async () => {
    setBusy(true);
    setImportErrors(null);
    try {
      const rows = data.rows.slice(hasHeader ? 1 : 0);
      const { data: res } = await api.post("/library/import-articles-mapped", { rows, mapping, mode: "append" });
      if (res.errors?.length) {
        setImportErrors({ created: res.created, skipped: res.skipped_duplicates, list: res.errors });
        setAdvanced(true);
        if (res.created > 0) toast.success(`${res.created} article(s) créé(s) — voir les lignes en erreur ci-dessous`);
      } else {
        toast.success(`Import : ${res.created} article(s) créé(s), ${res.skipped_duplicates} doublon(s) ignoré(s)`);
        onDone();
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Échec de l'import");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: "rgba(0,0,0,0.75)" }} data-testid="articles-import-wizard">
      <div className="card w-full max-w-5xl max-h-[90vh] overflow-auto p-6">
        <div className="flex items-start justify-between mb-4">
          <div>
            <h2 className="font-display text-2xl" style={{ fontWeight: 700 }}>Importer des articles</h2>
            <p className="text-sm text-secondary-text mt-1">
              {advanced
                ? <>{totalRows} ligne{totalRows > 1 ? "s" : ""} détectée{totalRows > 1 ? "s" : ""}. Indiquez à quoi correspond chaque colonne — au minimum <b>Description</b> + <b>Prix d&apos;achat HT</b> (ou Prix de vente).</>
                : <>{totalRows} article{totalRows > 1 ? "s" : ""} prêt{totalRows > 1 ? "s" : ""} à importer — rien à configurer.</>}
            </p>
          </div>
          <button onClick={onClose} className="btn btn-ghost btn-sm" data-testid="import-wizard-close"><X size={16} /></button>
        </div>

        {!advanced ? (
          <div data-testid="import-simple-view">
            <div className="rounded-lg p-4 mb-4 flex items-start gap-3"
                 style={{ background: "rgba(34,197,94,0.08)", border: "1px solid rgba(34,197,94,0.35)" }}>
              <div className="text-xl" style={{ color: "#22c55e" }}>✓</div>
              <div>
                <div className="text-sm font-semibold" style={{ color: "#22c55e" }}>Fichier reconnu automatiquement</div>
                <div className="text-xs mt-1.5 flex flex-wrap gap-x-4 gap-y-1" style={{ color: "var(--chalk)" }}>
                  {detected.map((d) => (
                    <span key={d.f}><b style={{ color: "#FFC400" }}>{fieldLabel(d.f)}</b> ← {d.cols.map(colName).join(" + ")}</span>
                  ))}
                </div>
              </div>
            </div>
            <div className="border rounded-lg divide-y" style={{ borderColor: "#3A3D42", "--tw-divide-opacity": 1 }}>
              {simplePreview.map((a, i) => (
                <div key={i} className="flex items-center justify-between gap-3 px-4 py-2.5" style={{ borderColor: "#3A3D42" }}>
                  <div className="min-w-0">
                    <div className="text-sm truncate">{a.desc || "—"}</div>
                    {a.code && <div className="text-[11px] font-mono" style={{ color: "var(--grey)" }}>{a.code}</div>}
                  </div>
                  <div className="text-sm font-mono whitespace-nowrap" style={{ color: "#FFC400" }}>{a.price || "—"}</div>
                </div>
              ))}
            </div>
            <div className="text-[11px] mt-2" style={{ color: "var(--grey)" }}>
              Aperçu des 3 premiers articles — {totalRows} seront importés. Les références déjà existantes sont ignorées.
            </div>
          </div>
        ) : (
          <>
            <label className="flex items-center gap-2 text-sm mb-4 cursor-pointer">
              <input type="checkbox" checked={hasHeader} onChange={(e) => { setHasHeader(e.target.checked); setMapping(guessMapping(data.rows, e.target.checked)); }} data-testid="import-has-header" />
              La première ligne contient des titres (pas un article)
            </label>

            <div className="overflow-x-auto border rounded-lg" style={{ borderColor: "#3A3D42" }}>
              <table className="table" style={{ minWidth: nCols * 160 }}>
                <thead>
                  <tr>
                    {Array.from({ length: nCols }, (_, i) => (
                      <th key={i} className="p-2">
                        <select value={mapping[i] || "ignore"} onChange={(e) => setCol(i, e.target.value)}
                          className="input text-xs py-1.5 w-full" data-testid={`col-map-${i}`}
                          style={mapping[i] ? { borderColor: "#FFC400", color: "#FFC400" } : {}}>
                          {IMPORT_FIELDS.map((f) => <option key={f.v} value={f.v}>{f.label}</option>)}
                        </select>
                        {hasHeader && <div className="text-[10px] mt-1 text-[var(--grey)] truncate font-normal">{data.rows[0]?.[i]}</div>}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {preview.map((r, ri) => (
                    <tr key={ri}>
                      {Array.from({ length: nCols }, (_, ci) => (
                        <td key={ci} className="text-xs max-w-[220px] truncate" title={r[ci] || ""}>{r[ci] || <span className="text-[var(--grey)]">—</span>}</td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {totalRows > 5 && <div className="text-[11px] text-[var(--grey)] mt-2">Aperçu des 5 premières lignes — {totalRows} seront importées.</div>}
          </>
        )}

        {importErrors && (
          <div className="rounded-lg p-4 mt-4" data-testid="import-errors-box"
               style={{ background: "rgba(225,6,0,0.08)", border: "1px solid rgba(225,6,0,0.4)" }}>
            <div className="text-sm font-semibold mb-2" style={{ color: "#FF6B66" }}>
              {importErrors.created} créé(s) · {importErrors.skipped} doublon(s) · {importErrors.list.length} ligne(s) en erreur :
            </div>
            <div className="max-h-40 overflow-auto space-y-1">
              {importErrors.list.map((e, i) => (
                <div key={i} className="text-xs" style={{ color: "var(--chalk)" }}>
                  Ligne {e.row} : {e.error}
                </div>
              ))}
            </div>
            <div className="text-[11px] mt-2" style={{ color: "var(--grey)" }}>
              Corrigez l'association des colonnes ci-dessus puis réessayez, ou fermez si le reste vous convient.
            </div>
          </div>
        )}

        <div className="flex justify-between items-center mt-5">
          {advanced ? (
            <div className="text-xs" style={{ color: canImport ? "#22c55e" : "#E10600" }}>
              {canImport ? "✓ Prêt à importer" : "Associez au moins Description + un prix"}
            </div>
          ) : (
            <button onClick={() => setAdvanced(true)} className="btn btn-ghost btn-sm text-xs" data-testid="import-show-advanced">
              Ajuster les colonnes (avancé)
            </button>
          )}
          <div className="flex gap-2">
            <button onClick={onClose} className="btn btn-outline">Annuler</button>
            <button onClick={doImport} disabled={!canImport || busy} className="btn btn-accent" data-testid="import-wizard-submit" style={!canImport || busy ? { opacity: 0.4, cursor: "not-allowed" } : {}}>
              {busy ? "Import en cours..." : `Importer ${totalRows} article${totalRows > 1 ? "s" : ""}`}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
